import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { api, track } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, RefreshCw, Save, Upload } from "lucide-react";
import AssignJob from "@/components/AssignJob";
import TeamPanel from "@/components/TeamPanel";
import SlotBlocker from "@/components/SlotBlocker";
import AdminAlerts from "@/components/AdminAlerts";
import DispatchInfo from "@/components/DispatchInfo";
import CustomerWa from "@/components/CustomerWa";

const TABS = [
  { id: "bookings", label: "Paid bookings" },
  { id: "unpaid", label: "Unpaid attempts" },
  { id: "team", label: "CHELA team" },
  { id: "slots", label: "Close slots" },
  { id: "waitlist", label: "Waitlist" },
  { id: "partners", label: "Partners" },
  { id: "workers", label: "Workers" },
  { id: "media", label: "Media" },
  { id: "settings", label: "Settings" },
];

const STATUSES = ["New", "Called", "Confirmed", "Done"];
const LISTS = ["bookings", "waitlist", "partners", "workers"];
const MEDIA_SLOTS = ["hero_video", "why_choose_us_video", "worker_video", "customer_personal_video", "customer_parents_video", "customer_errand_video", "customer_homehelp_video", "customer_events_video", "customer_shopping_video"];
const MEDIA_FIELDS = [["url", "Video URL — 1080p MP4 (or YouTube/Vimeo)"], ["url_720", "720p MP4 (optional)"], ["url_480", "480p MP4 (optional)"], ["webm", "WebM 1080p (optional)"], ["poster", "Thumbnail URL (min 1280px wide)"], ["caption_hi", "Hindi caption"], ["caption_en", "English subtitle"], ["source", "Source"], ["licence", "Licence"]];

function StatusSelect({ kind, item, onChange }) {
  return (
    <select data-testid={`${kind}-status-${item.id}`} value={item.status} onChange={(e) => onChange(kind, item.id, e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0D8] text-sm font-semibold bg-white">
      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

const Src = ({ v }) => (v ? <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#E8F0FB] text-[#2457A6]">SRC: {v}</span> : null);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [partners, setPartners] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [settings, setSettings] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, b, w, p, st, wk] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/bookings"), api.get("/admin/waitlist"),
        api.get("/admin/partners"), api.get("/settings"), api.get("/admin/workers"),
      ]);
      setStats(s.data); setBookings(b.data); setWaitlist(w.data); setPartners(p.data); setSettings(st.data); setWorkers(wk.data);
    } catch (e) { toast.error("Failed to load data"); }
  }, []);

  useEffect(() => {
    api.get("/auth/me").then(() => { setAuthed(true); loadAll(); })
      .catch(() => { setAuthed(false); navigate("/admin"); });
  }, [navigate, loadAll]);

  const logout = async () => { await api.post("/auth/logout").catch(() => {}); navigate("/admin"); };

  const setters = { bookings: setBookings, waitlist: setWaitlist, partners: setPartners, workers: setWorkers };
  const updateStatus = async (kind, id, status) => {
    try {
      await api.patch(`/admin/${kind}/${id}`, { status });
      setters[kind]((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const exportCsv = async (kind) => {
    try {
      const r = await api.get(`/admin/export/${kind}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `chela-${kind}.csv`;
      a.click();
    } catch (e) { toast.error("Export failed"); }
  };

  const checkPay = async (id) => {
    try {
      const { data } = await api.post(`/admin/bookings/${id}/check-payment`);
      if (data.payment_status === "paid") {
        setBookings((xs) => xs.map((x) => (x.id === id ? { ...x, payment_status: "paid", status: "Confirmed" } : x)));
        toast.success("Payment found on Razorpay. Moved to Paid bookings.");
      } else toast.error(data.detail || "Not paid");
    } catch (e) { toast.error("Could not check Razorpay"); }
  };
  const shownBookings = bookings.filter((b) => (tab === "unpaid" ? b.payment_status !== "paid" : b.payment_status === "paid"));

  if (authed === null) return <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center text-[#5A5D65]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="bg-[#121316] text-white">
        <div className="chela-container h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><Logo dark size="text-2xl" /><span className="text-xs text-white/50 font-bold uppercase tracking-wider">Admin</span></div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={loadAll} className="h-9 px-3 rounded-full bg-white/10 text-sm font-semibold inline-flex items-center gap-2 hover:bg-white/20"><RefreshCw size={14} /> Refresh</button>
            <button data-testid="admin-logout" onClick={logout} className="h-9 px-3 rounded-full bg-[#E05624] text-sm font-bold inline-flex items-center gap-2"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="chela-container py-8">
        <AdminAlerts onChange={loadAll} />
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
            {[
              ["Paid bookings", stats.bookings], ["Unpaid attempts", stats.new_bookings],
              ["Revenue ₹", stats.revenue], ["Waitlist", stats.waitlist],
              ["Partners", stats.partners], ["Workers", stats.workers], ["Book clicks", stats.book_clicks], ["WA clicks", stats.whatsapp_clicks], ["Page views", stats.page_views], ["Form submits", stats.submissions],
            ].map(([l, v]) => (
              <div key={l} className="chela-card rounded-2xl p-4" style={{ transform: "none" }}>
                <div className="text-2xl font-display font-extrabold text-[#121316]">{v}</div>
                <div className="text-xs text-[#5A5D65] font-semibold">{l}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map((tb) => (
            <button key={tb.id} data-testid={`tab-${tb.id}`} onClick={() => setTab(tb.id)} className={`h-10 px-5 rounded-full text-sm font-bold transition-colors ${tab === tb.id ? "bg-[#121316] text-white" : "bg-white border border-[#E5E0D8] text-[#5A5D65]"}`}>{tb.label}</button>
          ))}
          {LISTS.includes(tab) && (
            <button data-testid="admin-export" onClick={() => exportCsv(tab)} className="h-10 px-5 rounded-full text-sm font-bold bg-[#1B8A4B] text-white ml-auto">Export CSV (Excel / Google Sheets)</button>
          )}
        </div>

        {(tab === "bookings" || tab === "unpaid") && (
          <div className="space-y-3" data-testid={`admin-${tab}-list`}>
            {tab === "unpaid" && <p className="text-sm text-[#5A5D65]">People who filled the form but did not pay. These are NOT bookings. Do not send a CHELA. You can call them as leads.</p>}
            {shownBookings.length === 0 && <Empty label={tab === "unpaid" ? "No unpaid attempts." : "No paid bookings yet."} />}
            {shownBookings.map((b) => (
              <div key={b.id} className="chela-card rounded-2xl p-5" style={{ transform: "none" }}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-bold text-lg text-[#121316]">{b.name} · <span className="text-[#E05624]">{b.service}</span></div>
                    <div className="text-sm text-[#5A5D65] mt-1">📞 {b.phone} · 🏙️ {b.city} · ⏱️ {b.duration}</div>
                    <div className="text-sm text-[#5A5D65]">📅 {b.date || "-"} {b.time} · 📍 {b.location || "-"}</div>
                    {b.maps_link && <a data-testid={`admin-booking-map-${b.id}`} href={b.maps_link} target="_blank" rel="noreferrer" className="text-sm font-bold text-[#E05624] underline">Open live location on map</a>}
                    {b.description && <div className="text-sm text-[#3A3D45] mt-2">📝 {b.description}</div>}
                    {b.email && <div className="text-sm text-[#5A5D65] mt-1">✉️ {b.email}</div>}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {b.payment_status === "paid" && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#E8F7EE] text-[#1B8A4B]">PAID ₹{Math.round((b.amount || 0) / 100)}</span>}
                      {b.payment_status !== "paid" && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#FDECEC] text-[#C0392B]">NOT PAID — do not send a CHELA</span>}
                      {b.payment_status !== "paid" && <button data-testid={`admin-check-pay-${b.id}`} onClick={() => checkPay(b.id)} className="text-xs font-bold px-3 py-1 rounded-full border border-[#E5E0D8] bg-white">Check payment on Razorpay</button>}
                      {b.payment_status !== "paid" && b.pay_token && (
                        <a data-testid={`admin-pay-reminder-${b.id}`} target="_blank" rel="noreferrer" onClick={() => track("pay_reminder_sent", { booking_id: b.id })}
                          href={`https://wa.me/${(b.phone || "").replace(/\D/g, "").replace(/^(\d{10})$/, "91$1")}?text=${encodeURIComponent(`Namaste ${b.name} ji 🙏\nAapki CHELA booking (${b.service}, ${b.date} ${b.time}) ka payment abhi baaki hai. Payment ke baad hi CHELA confirm hota hai.\n\n👉 Yahan 1 tap mein pay karein: ${window.location.origin}/pay/${b.pay_token}\n\nHi ${b.name}, your CHELA booking is waiting. Pay here to confirm: ${window.location.origin}/pay/${b.pay_token}\n— CHELA`)}`}
                          className="text-xs font-bold px-3 py-1 rounded-full bg-[#25D366] text-white">Send pay reminder on WhatsApp</a>
                      )}
                      {b.referral_code && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#FFF1EC] text-[#E05624]">REF: {b.referral_code}</span>}
                      <Src v={b.src} />
                    </div>
                    {b.rating && <div data-testid={`admin-booking-rating-${b.id}`} className="mt-2 text-sm font-bold text-[#B7791F]">{"★".repeat(b.rating)}{"☆".repeat(5 - b.rating)} <span className="font-normal text-[#5A5D65]">{b.rating_comment}</span></div>}
                    {b.payment_status === "paid" && <CustomerWa b={b} />}
                    {b.payment_status === "paid" && <DispatchInfo b={b} reload={loadAll} />}
                    {b.payment_status === "paid" && <AssignJob b={b} workers={workers} onAssigned={(id, d) => setBookings((xs) => xs.map((x) => (x.id === id ? { ...x, ...d, status: "Confirmed", job_status: x.job_status || "assigned" } : x)))} />}
                    <div className="text-xs text-[#9A9DA5] mt-2">{new Date(b.created_at).toLocaleString()}</div>
                  </div>
                  <StatusSelect kind="bookings" item={b} onChange={updateStatus} />
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "waitlist" && (
          <div className="space-y-3" data-testid="admin-waitlist-list">
            {waitlist.length === 0 && <Empty label="No waitlist entries yet." />}
            {waitlist.map((w) => (
              <div key={w.id} className="chela-card rounded-2xl p-5" style={{ transform: "none" }}>
                <div className="flex flex-wrap items-start justify-between gap-3"><div className="font-display font-bold text-lg text-[#121316]">{w.name} · <span className="text-[#E05624]">{w.city}</span></div><StatusSelect kind="waitlist" item={w} onChange={updateStatus} /></div>
                <div className="text-sm text-[#5A5D65] mt-1">📞 {w.phone} <Src v={w.src} /></div>
                {w.use_case && <div className="text-sm text-[#3A3D45] mt-1">💬 {w.use_case}</div>}
                <div className="text-xs text-[#9A9DA5] mt-2">{new Date(w.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "partners" && (
          <div className="space-y-3" data-testid="admin-partners-list">
            {partners.length === 0 && <Empty label="No partner enquiries yet." />}
            {partners.map((p) => (
              <div key={p.id} className="chela-card rounded-2xl p-5" style={{ transform: "none" }}>
                <div className="flex flex-wrap items-start justify-between gap-3"><div className="font-display font-bold text-lg text-[#121316]">{p.business_name} · <span className="text-[#E05624]">{p.business_type || "—"}</span></div><StatusSelect kind="partners" item={p} onChange={updateStatus} /></div>
                <div className="text-sm text-[#5A5D65] mt-1">👤 {p.contact_name} · 📞 {p.phone} · 🏙️ {p.city}{p.area ? ` · 📍 ${p.area}` : ""} <Src v={p.src} /></div>
                {p.message && <div className="text-sm text-[#3A3D45] mt-1">💬 {p.message}</div>}
                {p.referral_code && <div className="mt-2"><span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#121316] text-white">CODE: {p.referral_code}</span> <span className="text-xs text-[#5A5D65]">— share with this store</span></div>}
                <div className="text-xs text-[#9A9DA5] mt-2">{new Date(p.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "workers" && (
          <div className="space-y-3" data-testid="admin-workers-list">
            {workers.length === 0 && <Empty label="No worker applications yet." />}
            {workers.map((w) => (
              <div key={w.id} className="chela-card rounded-2xl p-5" style={{ transform: "none" }}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-bold text-lg text-[#121316]">{w.name} · <span className="text-[#E05624]">{w.areas || w.area || "-"}</span></div>
                    <div className="text-sm text-[#5A5D65] mt-1">📞 {w.whatsapp} {w.is_adult ? "· 18+ ✓" : ""} {w.consent ? "· Consent ✓" : ""} <Src v={w.src} /></div>
                    <div className="text-sm text-[#5A5D65]">🕒 {(w.availability || []).join(", ") || "-"} · 🧰 {(w.work_types || []).join(", ") || "-"}</div>
                    <div className="text-xs text-[#9A9DA5] mt-2">{new Date(w.created_at).toLocaleString()}</div>
                  </div>
                  <StatusSelect kind="workers" item={w} onChange={updateStatus} />
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "team" && <TeamPanel />}
        {tab === "slots" && <SlotBlocker />}
        {tab === "media" && settings && <MediaPanel settings={settings} onSaved={setSettings} />}

        {tab === "settings" && settings && <SettingsPanel settings={settings} onSaved={setSettings} />}
      </div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="chela-card rounded-2xl p-10 text-center text-[#5A5D65]" style={{ transform: "none" }}>{label}</div>;
}

const adminInput = "w-full h-11 px-4 rounded-xl border border-[#E5E0D8] outline-none focus:border-[#E05624] text-sm";
const CHUNK = 4 * 1024 * 1024;

function UploadButton({ testid, accept, label, onDone }) {
  const [pct, setPct] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    try {
      let res;
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", id); fd.append("index", i); fd.append("total", total); fd.append("filename", file.name);
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
        res = await api.post("/admin/media/upload", fd);
        setPct(Math.round(((i + 1) / total) * 100));
      }
      if (res.data.processing) {
        setPct("conv");
        for (let k = 0; k < 150; k++) {
          await new Promise((r) => setTimeout(r, 4000));
          const { data: st } = await api.get(`/admin/media/upload/${res.data.processing}`);
          if (st.status === "done") { res = { data: { url: st.url } }; break; }
          if (st.status === "failed") { throw new Error(st.error || "Could not convert this video"); }
        }
        if (!res.data.url) { throw new Error("Video is taking too long. Try a shorter clip."); }
      }
      onDone(res.data.url);
      toast.success("Uploaded. Click Save media to publish it.");
    } catch (err) { toast.error(err.response?.data?.detail || err.message || "Upload failed"); } finally { setPct(null); }
  };
  return (
    <label className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[#E5E0D8] bg-white text-sm font-bold cursor-pointer hover:border-[#E05624]">
      <Upload size={16} /> {pct === "conv" ? "Preparing video for all phones…" : pct !== null ? `Uploading ${pct}%` : label}
      <input data-testid={testid} type="file" accept={accept} className="hidden" onChange={pick} disabled={pct !== null} />
    </label>
  );
}

function MediaPanel({ settings, onSaved }) {
  const [media, setMedia] = useState(settings.media || {});
  const [saving, setSaving] = useState(false);
  const setField = (slot, k, v) => setMedia((m) => ({ ...m, [slot]: { ...(m[slot] || {}), [k]: v } }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", { media });
      onSaved(data);
      toast.success("Media saved");
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-media">
      <p className="text-sm text-[#5A5D65]">Upload a phone video (MP4/MOV, max 200 MB) and a thumbnail, or paste an MP4, YouTube or Vimeo link. Then click Save media.</p>
      {MEDIA_SLOTS.map((slot) => (
        <div key={slot} className="chela-card rounded-2xl p-5 space-y-3" style={{ transform: "none" }}>
          <div className="font-display font-bold text-[#121316]">{slot} {!(media[slot]?.url) && <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-[#FCF4E6] text-[#8A6A2E]">EMPTY</span>}</div>
          {media[slot]?.url && /\.(mp4|webm|mov)$/i.test(media[slot].url) && <video data-testid={`media-${slot}-preview`} src={media[slot].url} poster={media[slot]?.poster} controls muted playsInline className="w-full max-w-sm rounded-xl bg-black" />}
          <div className="flex flex-wrap gap-2">
            <UploadButton testid={`media-${slot}-upload-video`} accept="video/mp4,video/quicktime,video/webm" label="Upload video" onDone={(u) => setField(slot, "url", u)} />
            <UploadButton testid={`media-${slot}-upload-poster`} accept="image/jpeg,image/png,image/webp" label="Upload thumbnail" onDone={(u) => setField(slot, "poster", u)} />
          </div>
          {MEDIA_FIELDS.map(([k, lbl]) => (
            <label key={k} className="block"><span className="block text-xs font-bold text-[#5A5D65] mb-1 uppercase">{lbl}</span>
              <input data-testid={`media-${slot}-${k}`} value={media[slot]?.[k] || ""} onChange={(e) => setField(slot, k, e.target.value)} className={adminInput} /></label>
          ))}
        </div>
      ))}
      <button data-testid="media-save" onClick={save} disabled={saving} className="h-13 px-7 py-3.5 rounded-full btn-saffron font-bold inline-flex items-center gap-2 disabled:opacity-60"><Save size={18} /> {saving ? "Saving…" : "Save media"}</button>
    </div>
  );
}

function SettingsPanel({ settings, onSaved }) {
  const [price, setPrice] = useState(settings.base_hourly_price);
  const [share, setShare] = useState(settings.worker_share);
  const [email, setEmail] = useState(settings.business_email);
  const [note, setNote] = useState(settings.pricing_note);
  const [cities, setCities] = useState(settings.cities);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", { base_hourly_price: Number(price), worker_share: Number(share), business_email: email, pricing_note: note, cities });
      onSaved(data);
      toast.success("Settings saved");
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const setCityStatus = (i, status) => setCities((cs) => cs.map((c, idx) => (idx === i ? { ...c, status } : c)));

  return (
    <div className="chela-card rounded-3xl p-6 sm:p-8 max-w-2xl space-y-6" style={{ transform: "none" }} data-testid="admin-settings">
      <div>
        <label className="block text-sm font-bold text-[#121316] mb-1.5">Standard price (₹/hr, used for online payment)</label>
        <input data-testid="settings-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="w-40 h-12 px-4 rounded-xl border border-[#E5E0D8] outline-none focus:border-[#E05624]" />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <label className="block"><span className="block text-sm font-bold text-[#121316] mb-1.5">Worker share (0-1)</span>
          <input data-testid="settings-share" type="number" step="0.05" value={share} onChange={(e) => setShare(e.target.value)} className={adminInput} /></label>
        <label className="block"><span className="block text-sm font-bold text-[#121316] mb-1.5">Business email</span>
          <input data-testid="settings-email" value={email} onChange={(e) => setEmail(e.target.value)} className={adminInput} /></label>
      </div>
      <div>
        <label className="block text-sm font-bold text-[#121316] mb-1.5">Pricing note</label>
        <textarea data-testid="settings-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full p-4 rounded-xl border border-[#E5E0D8] outline-none focus:border-[#E05624] resize-none" />
      </div>
      <div>
        <label className="block text-sm font-bold text-[#121316] mb-3">Cities</label>
        <div className="space-y-2">
          {cities.map((c, i) => (
            <div key={c.name} className="flex items-center justify-between bg-[#F3EFEA] rounded-xl px-4 py-2.5">
              <span className="font-semibold text-[#121316]">{c.name}</span>
              <select value={c.status} onChange={(e) => setCityStatus(i, e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0D8] text-sm bg-white font-semibold">
                <option value="live">live</option>
                <option value="coming_soon">coming_soon</option>
              </select>
            </div>
          ))}
        </div>
      </div>
      <button data-testid="settings-save" onClick={save} disabled={saving} className="h-13 px-7 py-3.5 rounded-full btn-saffron font-bold inline-flex items-center gap-2 disabled:opacity-60"><Save size={18} /> {saving ? "Saving…" : "Save settings"}</button>
    </div>
  );
}
